import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Video, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ACCEPT = "video/mp4,video/quicktime,video/webm,video/*";

function readDuration(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video"); v.preload = "metadata";
    const url = URL.createObjectURL(file);
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    v.src = url;
  });
}

export function VideoUploader({ value, onChange, disabled, testid = "video-uploader" }) {
  const inputRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [progress, setProgress] = useState(null);
  const [state, setState] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { api.get("/videos/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); return () => clearTimeout(pollRef.current); }, []);

  const poll = (videoId, tries = 0) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/videos/${videoId}/status`);
        if (data.state === "ready") { setState("ready"); onChange({ id: videoId, duration: data.duration }); return; }
        if (data.state === "error") { setState(null); onChange(null); toast.error(data.error === "duration_exceeded" ? "O vídeo excede 60 segundos" : "Não foi possível processar o vídeo"); return; }
        if (tries < 60) poll(videoId, tries + 1); else { setState(null); toast.error("O processamento está a demorar — tenta novamente"); }
      } catch { if (tries < 60) poll(videoId, tries + 1); }
    }, tries < 5 ? 2000 : 4000);
  };

  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Escolhe um ficheiro de vídeo");
    if (file.size > 200 * 1024 * 1024) return toast.error("Vídeo demasiado grande (máx. 200 MB) — grava em 1080p ou corta o vídeo");
    const dur = await readDuration(file);
    if (dur && dur > (cfg?.max_seconds || 60) + 0.5) return toast.error(`O vídeo tem ${Math.round(dur)}s — o máximo é ${cfg?.max_seconds || 60}s`);
    setState("uploading"); setProgress(0);
    let videoId = null;
    try {
      const { data } = await api.post("/videos/direct-upload");
      videoId = data.video_id;
      await new Promise((resolve, reject) => {
        const fd = new FormData(); fd.append("file", file);
        const xhr = new XMLHttpRequest(); xhr.open("POST", data.upload_url);
        xhr.timeout = 180000;
        xhr.upload.onprogress = (ev) => ev.lengthComputable && setProgress(Math.round((ev.loaded / ev.total) * 100));
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error("upload")));
        xhr.onerror = xhr.onabort = () => reject(new Error("interrupted"));
        xhr.ontimeout = () => reject(new Error("timeout"));
        xhr.send(fd);
      });
      setState("processing"); setProgress(null);
      poll(data.video_id);
    } catch (err) {
      setState(null); setProgress(null);
      if (videoId && !err?.response) api.delete(`/videos/${videoId}`).catch(() => {}); // liberta o upload pendente na Cloudflare
      const msg = err?.message === "interrupted" ? "Envio interrompido — mantém a app aberta até o vídeo ficar pronto e tenta de novo"
        : err?.message === "timeout" ? "O envio demorou demasiado — tenta com melhor ligação ou um vídeo mais pequeno" : null;
      toast.error(msg || formatApiError(err?.response?.data?.detail) || "Falha no envio do vídeo");
    }
  };

  const remove = async () => { clearTimeout(pollRef.current); if (value?.id) api.delete(`/videos/${value.id}`).catch(() => {}); onChange(null); setState(null); };

  if (cfg && !cfg.enabled) return null;
  return (
    <div data-testid={testid} className="inline-flex items-center gap-2">
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {!value && !state && (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled} data-testid={`${testid}-btn`}
                className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-200 hover:border-black disabled:opacity-50">
          <Video size={14} /> Vídeo (≤{cfg?.max_seconds || 60}s)
        </button>
      )}
      {state === "uploading" && <span data-testid={`${testid}-progress`} className="text-xs text-zinc-600 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> A enviar vídeo… {progress ?? 0}% <span className="text-zinc-400">· mantém a app aberta</span></span>}
      {state === "processing" && <span data-testid={`${testid}-processing`} className="text-xs text-zinc-600 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> A processar vídeo…</span>}
      {(value || state) && (
        <span className="inline-flex items-center gap-1 text-xs">
          {value && <span data-testid={`${testid}-ready`} className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold inline-flex items-center gap-1"><Video size={12} /> Vídeo pronto{value.duration ? ` · ${Math.round(value.duration)}s` : ""}</span>}
          <button type="button" onClick={remove} aria-label="Remover vídeo" data-testid={`${testid}-remove`} className="p-1 rounded-full hover:bg-zinc-100"><X size={14} /></button>
        </span>
      )}
    </div>
  );
}
